import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const CommentSection = ({ sessionInviteId, isAdmin = false }) => {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyAuthorName, setReplyAuthorName] = useState('');
  const [replyPassword, setReplyPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [isSecret, setIsSecret] = useState(false);
  const [viewingSecretId, setViewingSecretId] = useState(null);
  const [viewSecretPassword, setViewSecretPassword] = useState('');
  const [unlockedSecrets, setUnlockedSecrets] = useState({});

  useEffect(() => {
    if (sessionInviteId) {
      loadComments();
    }
  }, [sessionInviteId]);

  const loadComments = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('session_comments')
        .select('*')
        .eq('session_invite_id', sessionInviteId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const nested = buildNestedComments(data || []);
      setComments(nested);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const buildNestedComments = (flatComments) => {
    const map = {};
    const roots = [];

    flatComments.forEach(c => {
      map[c.id] = { ...c, replies: [] };
    });

    flatComments.forEach(c => {
      if (c.parent_id && map[c.parent_id]) {
        map[c.parent_id].replies.push(map[c.id]);
      } else if (!c.parent_id) {
        roots.push(map[c.id]);
      }
    });

    return roots;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (!isAdmin && (!authorName.trim() || !password.trim())) {
      alert('이름, 비밀번호, 내용을 모두 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isAdmin) {
        const { error } = await supabase
          .from('session_comments')
          .insert({
            session_invite_id: sessionInviteId,
            author_name: '관리자',
            password_hash: 'admin',
            content: newComment.trim(),
            is_secret: false,
            is_admin_reply: true
          });

        if (error) throw error;
      } else {
        const passwordHash = await hashPassword(password);

        const { error } = await supabase
          .from('session_comments')
          .insert({
            session_invite_id: sessionInviteId,
            author_name: authorName.trim(),
            password_hash: passwordHash,
            content: newComment.trim(),
            is_secret: isSecret,
            is_admin_reply: false
          });

        if (error) throw error;
      }

      setNewComment('');
      setPassword('');
      setIsSecret(false);
      await loadComments();
    } catch (err) {
      console.error('Failed to submit comment:', err);
      alert(err?.message || '댓글 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReply = async (parentId) => {
    if (!replyContent.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    if (isAdmin) {
      setIsSubmitting(true);
      try {
        const { error } = await supabase
          .from('session_comments')
          .insert({
            session_invite_id: sessionInviteId,
            parent_id: parentId,
            author_name: '관리자',
            password_hash: 'admin',
            content: replyContent.trim(),
            is_admin_reply: true
          });

        if (error) throw error;

        setReplyingTo(null);
        setReplyContent('');
        await loadComments();
      } catch (err) {
        console.error('Failed to submit reply:', err);
        alert(err?.message || '답글 등록에 실패했습니다.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!replyAuthorName.trim() || !replyPassword.trim()) {
        alert('이름과 비밀번호를 입력해주세요.');
        return;
      }

      setIsSubmitting(true);
      try {
        const passwordHash = await hashPassword(replyPassword);

        const { error } = await supabase
          .from('session_comments')
          .insert({
            session_invite_id: sessionInviteId,
            parent_id: parentId,
            author_name: replyAuthorName.trim(),
            password_hash: passwordHash,
            content: replyContent.trim(),
            is_admin_reply: false
          });

        if (error) throw error;

        setReplyingTo(null);
        setReplyContent('');
        setReplyAuthorName('');
        setReplyPassword('');
        await loadComments();
      } catch (err) {
        console.error('Failed to submit reply:', err);
        alert(err?.message || '답글 등록에 실패했습니다.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEdit = async (commentId) => {
    if (!editContent.trim() || !editPassword.trim()) {
      alert('비밀번호와 내용을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const passwordHash = await hashPassword(editPassword);

      const { data: existing } = await supabase
        .from('session_comments')
        .select('password_hash')
        .eq('id', commentId)
        .single();

      if (existing?.password_hash !== passwordHash && !isAdmin) {
        alert('비밀번호가 일치하지 않습니다.');
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase
        .from('session_comments')
        .update({ content: editContent.trim() })
        .eq('id', commentId);

      if (error) throw error;

      setEditingId(null);
      setEditContent('');
      setEditPassword('');
      await loadComments();
    } catch (err) {
      console.error('Failed to edit comment:', err);
      alert(err?.message || '수정에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewSecret = async (commentId) => {
    if (!viewSecretPassword.trim()) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    try {
      const passwordHash = await hashPassword(viewSecretPassword);

      const { data: existing } = await supabase
        .from('session_comments')
        .select('password_hash')
        .eq('id', commentId)
        .single();

      if (existing?.password_hash !== passwordHash) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }

      setUnlockedSecrets(prev => ({ ...prev, [commentId]: true }));
      setViewingSecretId(null);
      setViewSecretPassword('');
    } catch (err) {
      console.error('Failed to verify password:', err);
      alert('확인에 실패했습니다.');
    }
  };

  const handleDelete = async (commentId) => {
    if (!deletePassword.trim() && !isAdmin) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const passwordHash = isAdmin ? 'admin' : await hashPassword(deletePassword);

      const { error } = await supabase
        .rpc('soft_delete_session_comment', {
          p_comment_id: commentId,
          p_password_hash: passwordHash
        });

      if (error) throw error;

      setDeletingId(null);
      setDeletePassword('');
      await loadComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert(err?.message || '삭제에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderComment = (comment, depth = 0) => {
    const isEditing = editingId === comment.id;
    const isDeleting = deletingId === comment.id;
    const isReplying = replyingTo === comment.id;
    const isSecretLocked = Boolean(comment.is_secret) && !unlockedSecrets?.[comment.id];
    const isViewingSecret = viewingSecretId === comment.id;

    return (
      <div key={comment.id} className={`${depth > 0 ? 'ml-4 md:ml-8 border-l-2 border-border pl-4' : ''}`}>
        <div className={`bg-muted rounded-lg border border-border p-4 ${comment.is_admin_reply ? 'bg-primary/5 border-primary/20' : ''} ${comment.is_secret ? 'border-warning/30' : ''}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${comment.is_admin_reply ? 'bg-primary text-primary-foreground' : 'bg-accent/20 text-accent'}`}>
                {comment.is_admin_reply ? <Icon name="Shield" size={16} /> : comment.author_name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p className={`text-sm font-medium ${comment.is_admin_reply ? 'text-primary' : 'text-foreground'}`}>
                  {comment.author_name}
                  {comment.is_admin_reply && <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">관리자</span>}
                  {comment.is_secret && <span className="ml-1 text-xs bg-warning/10 text-warning px-1.5 py-0.5 rounded flex-inline items-center gap-0.5"><Icon name="Lock" size={10} className="inline" /> 비밀</span>}
                </p>
                <p className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!comment.is_admin_reply && (
                <button
                  onClick={() => {
                    setEditingId(comment.id);
                    setEditContent(comment.content);
                  }}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-card transition-athletic"
                >
                  <Icon name="Pencil" size={14} />
                </button>
              )}
              {(!comment.is_admin_reply || isAdmin) && (
                <button
                  onClick={() => setDeletingId(comment.id)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-athletic"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              )}
            </div>
          </div>

          {isEditing ? (
            <div className="mt-3 space-y-2">
              <textarea
                className="w-full min-h-[80px] rounded-md bg-card border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  placeholder="비밀번호 확인"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  className="flex-1"
                />
                <Button size="sm" onClick={() => handleEdit(comment.id)} disabled={isSubmitting}>
                  수정
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditPassword(''); }}>
                  취소
                </Button>
              </div>
            </div>
          ) : isDeleting ? (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-error">정말 삭제하시겠습니까?</p>
              {!isAdmin && (
                <Input
                  type="password"
                  placeholder="비밀번호 확인"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              )}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="text-error border-error hover:bg-error/10" onClick={() => handleDelete(comment.id)} disabled={isSubmitting}>
                  삭제
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setDeletingId(null); setDeletePassword(''); }}>
                  취소
                </Button>
              </div>
            </div>
          ) : isSecretLocked ? (
            <div className="mt-3">
              {isViewingSecret ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">비밀번호를 입력하면 내용을 확인할 수 있습니다.</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="password"
                      placeholder="비밀번호"
                      value={viewSecretPassword}
                      onChange={(e) => setViewSecretPassword(e.target.value)}
                      className="flex-1"
                    />
                    <Button size="sm" onClick={() => handleViewSecret(comment.id)}>
                      확인
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setViewingSecretId(null); setViewSecretPassword(''); }}>
                      취소
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setViewingSecretId(comment.id)}
                  className="flex items-center gap-2 text-sm text-warning hover:text-warning/80 transition-athletic bg-warning/10 rounded-md px-3 py-2"
                >
                  <Icon name="Lock" size={16} />
                  비밀글입니다. 클릭하여 비밀번호를 입력하세요.
                </button>
              )}
            </div>
          ) : (
            <p className="mt-3 text-sm text-foreground whitespace-pre-wrap">{comment.content}</p>
          )}

          {!isEditing && !isDeleting && !isSecretLocked && (
            <div className="mt-3 pt-3 border-t border-border">
              {isReplying ? (
                <div className="space-y-2">
                  {!isAdmin && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="이름"
                        value={replyAuthorName}
                        onChange={(e) => setReplyAuthorName(e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="비밀번호"
                        value={replyPassword}
                        onChange={(e) => setReplyPassword(e.target.value)}
                      />
                    </div>
                  )}
                  <textarea
                    className="w-full min-h-[60px] rounded-md bg-card border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={isAdmin ? "관리자 답글을 입력하세요..." : "답글을 입력하세요..."}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleReply(comment.id)} disabled={isSubmitting}>
                      {isAdmin ? '관리자 답글' : '답글 등록'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>
                      취소
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setReplyingTo(comment.id)}
                  className="text-xs text-muted-foreground hover:text-primary transition-athletic flex items-center gap-1"
                >
                  <Icon name="Reply" size={14} />
                  답글
                </button>
              )}
            </div>
          )}
        </div>

        {comment.replies?.length > 0 && (
          <div className="mt-2 space-y-2">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="MessageCircle" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-semibold text-foreground">문의하기</h2>
          <p className="text-xs caption text-muted-foreground">궁금한 점이 있으면 남겨주세요</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 space-y-3">
        {!isAdmin && (
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="이름 또는 닉네임"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="비밀번호 (수정/삭제용)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        )}
        <textarea
          className="w-full min-h-[100px] rounded-md bg-muted border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          placeholder={isAdmin ? "관리자 글을 입력하세요..." : "문의 내용을 입력하세요..."}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          required
        />
        {!isAdmin && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isSecret}
              onChange={(e) => setIsSecret(e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"
            />
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Icon name="Lock" size={14} />
              비밀글로 등록 (본인만 비밀번호로 확인 가능)
            </span>
          </label>
        )}
        <Button type="submit" disabled={isSubmitting} fullWidth>
          {isSubmitting ? '등록 중...' : isAdmin ? '관리자 글 등록' : '문의 등록'}
        </Button>
      </form>

      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">댓글을 불러오는 중...</p>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 bg-muted rounded-lg border border-border">
            <Icon name="MessageSquare" size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">아직 문의가 없습니다.</p>
            <p className="text-xs text-muted-foreground mt-1">첫 번째로 문의를 남겨보세요!</p>
          </div>
        ) : (
          comments.map(comment => renderComment(comment))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
